import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { map, of } from 'rxjs';
import { AuctionInfo, AuctionItemInfo, AuctionWatchInfo, BidStatusInfo, PurchaseResult } from './auction';
import { Nullable } from '../../classes/nullable';
import { AuctionChangesInfo } from '../../modules/edit-auction/edit-auction';
import { ImageBlob } from '../../classes/image-blob';
import { ImageBlobCacheService, imageSize } from '../imageblob-cache/imageblob-cache-svc.ng';
import { TransformDates } from '../../classes/transform-dates';

@Injectable({
    providedIn: 'root',
})
export class AuctionService {
    // #region Injects

    protected readonly http = inject(HttpClient);
    protected readonly imageBlobCache = inject(ImageBlobCacheService);

    // #endregion

    // #region Properties

    public selectedAuction = signal<AuctionInfo | null>(null);

    // #endregion

    // #region Close Auction

    @TransformDates
    closeAuction(auctionID: Nullable<number>) {
        return this.http.get<AuctionInfo>(`api/Auction/close/${auctionID}`).pipe(
            map((data) => {
                data.BannerImage = ImageBlob.FromBase64(data.BannerImage as any as string);
                if (data.BannerImage) this.imageBlobCache.add(data.AuctionID, 'banner', data.BannerImage);

                if (data?.AuctionItems?.length) {
                    data.AuctionItems.forEach((i) => {
                        i.MidSizePicture = ImageBlob.FromBase64(i.MidSizePicture as any as string);
                        i.Thumbnail = ImageBlob.FromBase64(i.Thumbnail as any as string);

                        // Store in cache (if exists)
                        if (i.MidSizePicture) this.imageBlobCache.add(i.AuctionItemID, 'midsize', i.MidSizePicture);
                        if (i.Thumbnail) this.imageBlobCache.add(i.AuctionItemID, 'thumbnail', i.Thumbnail);
                    });
                }

                return data;
            })
        );
    }

    // #endregion

    // #region Get All Auctions

    @TransformDates
    getAllAuctions() {
        return this.http.get<AuctionInfo[]>(`api/Auction`).pipe(
            map((data) => {
                if (data?.length) {
                    data.forEach((d) => {
                        d.BannerImage = ImageBlob.FromBase64(d.BannerImage as any as string);
                        if (d.BannerImage) this.imageBlobCache.add(d.AuctionID, 'banner', d.BannerImage);

                        if (d.AuctionItems?.length) {
                            d.AuctionItems.forEach((i) => {
                                i.MidSizePicture = ImageBlob.FromBase64(i.MidSizePicture as any as string);
                                i.Thumbnail = ImageBlob.FromBase64(i.Thumbnail as any as string);

                                // Store in cache (if exists)
                                if (i.MidSizePicture) this.imageBlobCache.add(i.AuctionItemID, 'midsize', i.MidSizePicture);
                                if (i.Thumbnail) this.imageBlobCache.add(i.AuctionItemID, 'thumbnail', i.Thumbnail);
                            });
                        }
                    });
                }

                return data;
            })
        );
    }

    // #endregion

    // #region Get Auction Info

    @TransformDates
    getAuctionInfo(auctionID: Nullable<number>) {
        return this.http.get<AuctionInfo>(`api/Auction/auction/${auctionID}`).pipe(
            map((data) => {
                data.BannerImage = ImageBlob.FromBase64(data.BannerImage as any as string);
                if (data.BannerImage) this.imageBlobCache.add(data.AuctionID, 'banner', data.BannerImage);

                if (data?.AuctionItems?.length) {
                    data.AuctionItems.forEach((i) => {
                        i.MidSizePicture = ImageBlob.FromBase64(i.MidSizePicture as any as string);
                        i.Thumbnail = ImageBlob.FromBase64(i.Thumbnail as any as string);

                        // Store in cache (if exists)
                        if (i.MidSizePicture) this.imageBlobCache.add(i.AuctionItemID, 'midsize', i.MidSizePicture);
                        if (i.Thumbnail) this.imageBlobCache.add(i.AuctionItemID, 'thumbnail', i.Thumbnail);
                    });
                }

                return data;
            })
        );
    }

    // #endregion

    // #region Get Auction Item Photo

    getAuctionItemPhoto(auctionItemID: Nullable<number>, size?: Nullable<imageSize>) {
        let photo: ImageBlob | null = null;

        if (auctionItemID != null && size != null) {
            // Check for the item in the cache.
            photo = this.imageBlobCache.get(auctionItemID, size);
        }

        if (photo != null) return of<ImageBlob | null>(photo);

        return this.http
            .get(`api/Auction/picture/${auctionItemID}/${size != null ? size : ''}`, {
                responseType: 'blob',
            })
            .pipe(
                map((data) => {
                    // Wrap img returned in an ImageBlob.
                    if (data == null) return null;

                    photo = new ImageBlob(data);

                    // Update cache.
                    if (auctionItemID != null && size != null) {
                        this.imageBlobCache.add(auctionItemID, size, photo);
                    }

                    return photo;
                })
            );
    }

    // #endregion

    // #region Get Watched Items

    getWatchedItemsForAction(auctionID: Nullable<number>) {
        return this.http.get<AuctionWatchInfo[]>(`api/Auction/watched/${auctionID != null ? auctionID : ''}`).pipe(
            map((data) => {
                data?.forEach((d) => {
                    if (d.Item) {
                        d.Item.MidSizePicture = ImageBlob.FromBase64(d.Item.MidSizePicture as any as string);
                        d.Item.Thumbnail = ImageBlob.FromBase64(d.Item.Thumbnail as any as string);

                        // Store in cache (if exists)
                        if (d.Item.MidSizePicture) this.imageBlobCache.add(d.Item.AuctionItemID, 'midsize', d.Item.MidSizePicture);
                        if (d.Item.Thumbnail) this.imageBlobCache.add(d.Item.AuctionItemID, 'thumbnail', d.Item.Thumbnail);
                    }
                });

                return data;
            })
        );
    }

    // #endregion

    // #region Place Bid

    placeBid(
        auctionID: Nullable<number>,
        auctionItemID: Nullable<number>,
        bid: Nullable<number>,
        useAutomaticBidding: Nullable<boolean>,
        maxAutomaticBid: Nullable<number>
    ) {
        return this.http
            .post<BidStatusInfo>(`api/Auction/bid`, {
                AuctionID: auctionID,
                AuctionItemID: auctionItemID,
                Bid: bid,
                UseAutomaticBidding: useAutomaticBidding,
                MaxAutomaticBid: maxAutomaticBid,
            })
            .pipe(
                map((data) => {
                    if (data?.AuctionItem) {
                        data.AuctionItem.MidSizePicture = ImageBlob.FromBase64(data.AuctionItem.MidSizePicture as any as string);
                        data.AuctionItem.Thumbnail = ImageBlob.FromBase64(data.AuctionItem.Thumbnail as any as string);

                        // Store in cache (if exists)
                        if (data.AuctionItem.MidSizePicture)
                            this.imageBlobCache.add(data.AuctionItem.AuctionItemID, 'midsize', data.AuctionItem.MidSizePicture);
                        if (data.AuctionItem.Thumbnail)
                            this.imageBlobCache.add(data.AuctionItem.AuctionItemID, 'thumbnail', data.AuctionItem.Thumbnail);
                    }

                    return data;
                })
            );
    }

    // #endregion

    // #region Purchase Item

    purchaseItem(auctionItemID: Nullable<number>, auctionID: Nullable<number>, price: Nullable<number>, quantity: Nullable<number>) {
        return this.http
            .post<PurchaseResult>(`api/Auction/buy`, {
                AuctionItemID: auctionItemID,
                AuctionID: auctionID,
                Price: price,
                Quantity: quantity,
            })
            .pipe(
                map((data) => {
                    if (data?.AuctionItem) {
                        data.AuctionItem.MidSizePicture = ImageBlob.FromBase64(data.AuctionItem.MidSizePicture as any as string);
                        data.AuctionItem.Thumbnail = ImageBlob.FromBase64(data.AuctionItem.Thumbnail as any as string);

                        // Store in cache (if exists)
                        if (data.AuctionItem.MidSizePicture)
                            this.imageBlobCache.add(data.AuctionItem.AuctionItemID, 'midsize', data.AuctionItem.MidSizePicture);
                        if (data.AuctionItem.Thumbnail)
                            this.imageBlobCache.add(data.AuctionItem.AuctionItemID, 'thumbnail', data.AuctionItem.Thumbnail);
                    }

                    return data;
                })
            );
    }

    // #endregion

    // #region Set Auction Item Favorite

    setAuctionItemFavorite(auctionItemID: Nullable<number>, status: Nullable<boolean>) {
        return this.http.patch<boolean>(`api/Auction/favorite`, {
            AuctionItemID: auctionItemID,
            Status: status,
        });
    }

    // #endregion

    // #region Update Picture

    updatePicture(auctionItemID: Nullable<number>, picture: Nullable<string>) {
        if (!picture) throw new Error('No picture data to submit');

        const chararr: number[] = [];
        const binary = atob(picture.split(',')[1]);

        for (let i = 0, j = binary.length; i < j; ++i) {
            chararr.push(binary.charCodeAt(i));
        }

        const bitsarr = new Uint8Array(chararr);

        // Make the form for uploading the picture.
        const fd = new FormData();
        fd.append('AuctionItemID', auctionItemID != null ? '' + auctionItemID : '');
        fd.append('Size', '' + bitsarr.length);
        fd.append('file', new Blob([bitsarr], { type: 'image/jpeg' }));

        return this.http.post<Blob | null>(`api/Auction/picture`, fd, { responseType: 'blob' as 'json' }).pipe(
            map((data) => {
                if (!data) return null;

                // Since we just updated the picture, remove any cached entries as they are no longer valid for this item.
                if (auctionItemID != null) {
                    this.imageBlobCache.remove(auctionItemID);
                }

                // Now, convert the data into an ImageBlob, and load it into the cache as a thumbnail (as this is what is returned by this method).
                // Wrap img returned in an ImageBlob.
                if (data == null) return null;

                const photo = new ImageBlob(data);

                // Update cache.
                if (auctionItemID != null) {
                    this.imageBlobCache.add(auctionItemID, 'thumbnail', photo);
                }

                return photo;
            })
        );
    }

    // #endregion

    // #region Upsert Auction

    @TransformDates
    upsertAuction(changes: AuctionChangesInfo) {
        return this.http
            .post<AuctionInfo>(`api/Auction`, {
                Auction: changes.Auction,
                Items: changes.Items,
            })
            .pipe(
                map((data) => {
                    if (data) {
                        data.AuctionItems?.forEach((ai) => {
                            // To be safe, we will invalidate the cache for all items in the returned data, and update the cache.
                            this.imageBlobCache.remove(ai.AuctionItemID);

                            [
                                { Picture: ai.FullSizePicture, Size: 'full' as imageSize },
                                { Picture: ai.MidSizePicture, Size: 'midsize' as imageSize },
                                { Picture: ai.Thumbnail, Size: 'thumbnail' as imageSize },
                            ].forEach((p) => {
                                if (!!p.Picture) {
                                    const photo = ImageBlob.FromBase64(p.Picture as any as string);

                                    // Update cache (if photo)
                                    if (photo) this.imageBlobCache.add(ai.AuctionItemID, p.Size, photo);

                                    // Update item.
                                    switch (p.Size) {
                                        case 'full':
                                            ai.FullSizePicture = photo;
                                            break;
                                        case 'midsize':
                                            ai.MidSizePicture = photo;
                                            break;
                                        case 'thumbnail':
                                            ai.Thumbnail = photo;
                                            break;
                                    }
                                }
                            });
                        });
                    }

                    return data;
                })
            );
    }

    // #endregion
}
